/**
 * Concurrency utilities for controlled batch processing
 *
 * Provides utilities for processing items in batches with controlled concurrency
 * to prevent overwhelming servers or resources.
 */

/**
 * Semaphore implementation for controlling concurrent access to resources
 */
export class Semaphore {
  private permits: number;
  private waitQueue: Array<() => void> = [];

  constructor(permits: number) {
    if (permits <= 0) {
      throw new Error('Semaphore permits must be positive');
    }
    this.permits = permits;
  }

  /**
   * Acquire a permit from the semaphore
   * @returns Promise that resolves when a permit is available
   */
  async acquire(): Promise<void> {
    if (this.permits > 0) {
      this.permits--;
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      this.waitQueue.push(resolve);
    });
  }

  /**
   * Release a permit back to the semaphore
   */
  release(): void {
    if (this.waitQueue.length > 0) {
      const resolve = this.waitQueue.shift()!;
      resolve();
    } else {
      this.permits++;
    }
  }

  /**
   * Get the current number of available permits
   */
  get availablePermits(): number {
    return this.permits;
  }

  /**
   * Get the number of waiting operations
   */
  get waitingCount(): number {
    return this.waitQueue.length;
  }
}

/**
 * Options for batch processing with concurrency control
 */
export interface BatchProcessingOptions {
  /** Maximum number of concurrent operations (default: 5) */
  maxConcurrent?: number;
  /** Number of items to process in each batch (default: 10) */
  batchSize?: number;
  /** Delay between batches in milliseconds (default: 0) */
  delayBetweenBatches?: number;
}

/**
 * Process a batch of items with controlled concurrency
 *
 * @param items - Items to process
 * @param processor - Function to process each item
 * @param maxConcurrent - Maximum concurrent operations
 * @returns Promise that resolves when all items in the batch are processed
 */
async function processBatchWithConcurrencyLimit<T>(
  items: T[],
  processor: (item: T) => Promise<void>,
  maxConcurrent: number
): Promise<void> {
  const semaphore = new Semaphore(maxConcurrent);

  await Promise.all(
    items.map(async (item) => {
      await semaphore.acquire();
      try {
        await processor(item);
      } finally {
        semaphore.release();
      }
    })
  );
}

/**
 * Process batches of items with controlled concurrency
 *
 * @param items - Array of items to process
 * @param batchProcessor - Function to process each batch
 * @param options - Batch processing options
 * @param onProgress - Optional progress callback
 */
export async function processBatchesWithConcurrency<T>(
  items: T[],
  batchProcessor: (batch: T[]) => Promise<void>,
  options: BatchProcessingOptions = {},
  onProgress?: (completed: number, total: number) => void
): Promise<void> {
  const { maxConcurrent = 5, batchSize = 10 } = options;

  let completed = 0;
  const total = items.length;

  // Create batches
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    batches.push(items.slice(i, i + batchSize));
  }

  // Process batches with controlled concurrency
  await processBatchWithConcurrencyLimit(
    batches,
    async (batch) => {
      await batchProcessor(batch).catch((error) => {
        if (error.message.includes('unique constraint violations'))
          console.log(`Unique constraint violations for ${batchProcessor.name}:`, error);
        else console.warn(`Error processing batch for ${batchProcessor.name}:`, error.message);
      });
      completed += batch.length;
      if (onProgress) {
        onProgress(completed, total);
      }
    },
    maxConcurrent
  );
}
